import {
  ArticleBullets,
  ArticleImage,
  ArticleLayout,
  ArticleParagraph,
  ArticleSection,
  QuickVerdict,
} from '../../../components/marketing/ArticleLayout';
import {
  ARTICLE_IMAGES,
  ARTICLE_SUMMARY_BY_SLUG,
} from '../../../lib/content/articles';

const article = ARTICLE_SUMMARY_BY_SLUG['how-we-turn-transaction-cleanup-into-better-planning'];

export default function TransactionCleanupIntoPlanningPage() {
  return (
    <ArticleLayout
      article={article}
      relatedSlugs={['budgeting-from-take-home-pay-not-gross-fantasy', 'your-daily-freedom-number']}
    >
      <QuickVerdict
        title="Quick take"
        items={[
          'Transaction review matters more when it improves the household model instead of just cleaning a feed.',
          'Per Diem uses categorization work to sharpen the budget, the daily freedom number, and the long-term plan.',
          'The same correction becomes more valuable when it informs multiple parts of the product at once.',
        ]}
      />

      <ArticleSection title="Transaction cleanup is tedious when it feels disconnected">
        <ArticleParagraph>
          Most people do not dislike transaction review because categorization is inherently awful.
          They dislike it because it often feels like janitorial work with no obvious payoff beyond a
          prettier expense chart.
        </ArticleParagraph>
        <ArticleParagraph>
          The job becomes more meaningful when each correction improves the quality of the household’s
          budgeting and planning system.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileLedger}
        alt="Per Diem mobile transaction ledger screen"
        caption="Transaction review becomes more useful when the output feeds real household guidance rather than living as a separate recordkeeping chore."
        variant="phone"
      />

      <ArticleSection title="Why category accuracy matters">
        <ArticleBullets
          items={[
            'Budgets become more believable when recurring spending lands in the right places.',
            'Daily spending guidance is more trustworthy when the product has a better read on household behavior.',
            'Long-term planning improves when current spending is represented honestly rather than smoothed over.',
          ]}
        />
      </ArticleSection>

      <ArticleSection title="Cleanup work should compound">
        <ArticleParagraph>
          This is the core idea. If the household takes time to correct transactions, that effort
          should pay off across the whole system. The product should not ask for meticulous review
          and then confine the benefit to one tab.
        </ArticleParagraph>
        <ArticleParagraph>
          Per Diem tries to make transaction cleanup feel more worthwhile by connecting it to the
          budget and planning layers where the household actually makes decisions.
        </ArticleParagraph>
      </ArticleSection>

      <ArticleImage
        source={ARTICLE_IMAGES.mobileBudget}
        alt="Per Diem mobile budget screen"
        caption="Better transaction data matters because budgeting is one of the places where households immediately feel the difference."
        variant="phone"
      />

      <ArticleSection title="The job is not bookkeeping for its own sake">
        <ArticleParagraph>
          The goal is not to turn the household into its own accounting department. The goal is to
          give the household a financial model that learns from each small bit of cleanup work and
          becomes more useful as a result.
        </ArticleParagraph>
        <ArticleParagraph>
          That is what turns transaction review from a maintenance burden into planning leverage.
        </ArticleParagraph>
      </ArticleSection>
    </ArticleLayout>
  );
}
